import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ErrorBufferService } from './error-buffer.service';

/**
 * Set on the direct-to-S3 screenshot upload so this interceptor leaves it alone.
 *
 * A URL check cannot do this: the S3 host in a presigned POST is not our API and carries none of
 * `/bug-report`, so matching on the URL the way the report and screenshot endpoints are matched
 * below would miss it entirely. A context token travels with the request through the interceptor
 * chain without being sent over the wire, so the upload can mark itself without the interceptor
 * needing to know what an S3 host looks like.
 */
export const SKIP_HTTP_FAILURE_BUFFER = new HttpContextToken<boolean>(() => false);

/**
 * Records failed HTTP calls so a bug report can say what the app was doing when it broke.
 *
 * A second interceptor rather than a change to `http-request-interceptor.ts`, which is the only
 * other one and is responsible for authentication. Failure recording has nothing to do with
 * attaching a token, and that file is load-bearing enough already.
 *
 * It only observes. It does not retry, does not swallow, and does not show a snackbar: error
 * display stays where this app already puts it, in each service's own `catchError`.
 *
 * **Method, URL, status and message only.** No headers, because the request carries a bearer
 * token, and no bodies, because both directions can carry customer information.
 */
@Injectable()
export class HttpFailureInterceptor implements HttpInterceptor {
  private readonly buffer = inject(ErrorBufferService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      tap({
        error: (error: unknown) => {
          if (!(error instanceof HttpErrorResponse)) return;
          // Reporting a bug must never record its own submission failing; that would displace the
          // failures the user is actually reporting and tell a triager nothing. Singular, not
          // plural: matches the v2 communication API's '/communication/bug-report' as well as the
          // Back Office's older '/api/v1/bug-reports', so a future path change is less likely to
          // silently slip past this guard again.
          if (req.url.includes('/bug-report')) return;
          // Covers the direct-to-S3 screenshot upload, whose URL matches none of the above.
          if (req.context.get(SKIP_HTTP_FAILURE_BUFFER)) return;
          this.buffer.recordHttpFailure({
            at: new Date().toISOString(),
            method: req.method,
            // Path and query only. An absolute URL adds a host the triager already knows from the
            // report's own environment, and eats the length cap.
            url: pathOf(req.url),
            status: error.status,
            ...(error.statusText ? { message: error.statusText } : {}),
          });
        },
      }),
    );
  }
}

function pathOf(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}
